"use client"

/**
 * Live preview surface for /tools/mowing-calculator.
 * Big deck-height + frequency readout, ruler diagram of the cut,
 * 1/3-rule callout, and a 12-month mowing-intensity heat strip.
 */

import type { MowingPlan } from "@/lib/mowing-plan"
import {
  YearCalendar,
  type YearCalendarCell,
} from "@/components/tools/shared/year-calendar"

const M_INK = "#111827"
const M_INK2 = "#374151"
const M_RULE = "#e5e7eb"
const M_BRAND = "#16a34a"
const M_BRAND_700 = "#15803d"
const M_BRAND_50 = "#f0fdf4"
const M_BRAND_100 = "#dcfce7"
const M_BG_ALT = "#f9fafb"
const M_MUTED = "#6b7280"

export interface MowingPreviewProps {
  plan: MowingPlan
  compact?: boolean
}

export function MowingPreview({ plan, compact = false }: MowingPreviewProps) {
  const today = new Date()
  const thisMonth = today.getMonth()

  if (compact) {
    return (
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          border: `1px solid ${M_RULE}`,
          boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
          padding: 14,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: M_BRAND_700,
            marginBottom: 6,
          }}
        >
          Set your mower to
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
          <span
            style={{
              fontSize: 28,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: "-0.025em",
              color: M_INK,
            }}
          >
            {fmtH(plan.idealH)}
            <span style={{ fontSize: 18, color: M_BRAND }}>″</span>
          </span>
          <span style={{ fontSize: 12, color: M_MUTED, marginLeft: "auto" }}>
            mow every {plan.freqDays} d
          </span>
        </div>
        <YearCalendar
          variant="compact"
          months={toMowingCells(plan)}
          thisMonth={thisMonth}
          ringColors={{ today: M_INK2 }}
          ariaLabel="12-month mowing intensity"
        />
      </div>
    )
  }

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        border: `1px solid ${M_RULE}`,
        boxShadow: "0 12px 40px rgba(0,0,0,0.04)",
        padding: "24px 24px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 20,
        color: M_INK,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: M_BRAND_700,
            marginBottom: 8,
          }}
        >
          Set your mower to
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <span
            style={{
              fontSize: 44,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: "-0.025em",
              color: M_INK,
            }}
          >
            {fmtH(plan.idealH)}
            <span style={{ fontSize: 28, color: M_BRAND }}>″</span>
          </span>
          <span style={{ fontSize: 14, color: M_MUTED, marginLeft: 6 }}>
            and mow every
          </span>
          <span
            style={{
              fontSize: 26,
              fontWeight: 700,
              color: M_INK,
              letterSpacing: "-0.02em",
            }}
          >
            {plan.freqDays}
          </span>
          <span style={{ fontSize: 14, color: M_MUTED }}>days</span>
        </div>
      </div>

      <RulerDiagram plan={plan} />

      <div
        style={{
          background: M_BRAND_50,
          border: `1px solid ${M_BRAND_100}`,
          borderRadius: 8,
          padding: "12px 14px",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              background: M_BRAND,
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontSize: 11,
              fontWeight: 800,
            }}
          >
            ⅓
          </span>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: M_INK }}>
              The 1/3 rule
            </div>
            <div
              style={{ fontSize: 13, color: M_INK2, marginTop: 2, lineHeight: 1.5 }}
            >
              Never remove more than a third of the blade in one mow. So when your
              grass hits <strong>{plan.oneThirdMax.toFixed(1)}″</strong>, cut back to{" "}
              <strong>{fmtH(plan.idealH)}″</strong>.
            </div>
          </div>
        </div>
      </div>

      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: M_INK2,
            marginBottom: 10,
          }}
        >
          Mowing season
        </div>
        <YearCalendar
          variant="full"
          months={toMowingCells(plan)}
          thisMonth={thisMonth}
          ringColors={{ today: M_INK2 }}
          ariaLabel="12-month mowing intensity"
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 12,
            fontSize: 11.5,
            color: M_MUTED,
            flexWrap: "wrap",
          }}
        >
          <Legend swatch="rgba(22,163,74,1)" label="Every 5 d" />
          <Legend swatch="rgba(22,163,74,0.75)" label="Weekly" />
          <Legend swatch="rgba(22,163,74,0.45)" label="Biweekly" />
          <Legend swatch={M_BG_ALT} label="Dormant" />
        </div>
      </div>
    </div>
  )
}

function fmtH(h: number) {
  // Trim trailing zeros: 2.0 → "2", 1.5 → "1.5"
  return h.toFixed(2).replace(/\.?0+$/, "")
}

function mowFreqAlpha(freq: number): number {
  if (freq <= 5) return 1
  if (freq <= 7) return 0.75
  if (freq <= 14) return 0.45
  return 0.15
}

function toMowingCells(plan: MowingPlan): YearCalendarCell[] {
  return plan.growingMonths.map(m => ({
    i: m.i,
    label: m.label,
    fill: m.active ? `rgba(22,163,74,${mowFreqAlpha(m.freq)})` : M_BG_ALT,
  }))
}

function RulerDiagram({ plan }: { plan: MowingPlan }) {
  const maxScale = Math.max(plan.oneThirdMax + 0.5, 5)
  const W = 360
  const H = 220
  const ground = H - 24
  const top = 14
  const usableH = ground - top
  const yAt = (inches: number) => ground - (inches / maxScale) * usableH
  const cutY = yAt(plan.idealH)
  const tipY = yAt(plan.oneThirdMax)

  const ticks: number[] = []
  for (let i = 0; i <= Math.ceil(maxScale); i++) ticks.push(i)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
      <rect x={0} y={ground} width={W} height={H - ground} fill="#a16207" opacity="0.15" />
      <line x1={0} y1={ground} x2={W} y2={ground} stroke="#a16207" strokeWidth="1.5" />

      <g transform="translate(20, 0)">
        <line x1={0} y1={top} x2={0} y2={ground} stroke={M_INK2} strokeWidth="1.5" />
        {ticks.map((t) => {
          const y = yAt(t)
          return (
            <g key={t}>
              <line x1={0} y1={y} x2={8} y2={y} stroke={M_INK2} strokeWidth="1" />
              <text
                x={-6}
                y={y + 3}
                textAnchor="end"
                fontSize="9"
                fill={M_MUTED}
              >
                {t}″
              </text>
            </g>
          )
        })}
      </g>

      <g transform="translate(60, 0)">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
          const x = i * 36
          const lean = Math.sin(i * 0.7) * 4
          const tipHere = tipY + Math.sin(i * 1.3) * 4
          return (
            <path
              key={i}
              d={`M ${x} ${ground} Q ${x + lean} ${(ground + tipHere) / 2} ${x + lean * 2} ${tipHere}`}
              stroke={M_BRAND}
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
              opacity={0.85}
            />
          )
        })}
      </g>

      <g>
        <line
          x1={50}
          y1={cutY}
          x2={W - 14}
          y2={cutY}
          stroke={M_INK}
          strokeWidth="1.5"
          strokeDasharray="6 4"
        />
        <rect x={W - 110} y={cutY - 11} width={100} height={22} rx={11} fill={M_INK} />
        <text
          x={W - 60}
          y={cutY + 4}
          textAnchor="middle"
          fontSize="11.5"
          fontWeight="700"
          fill="#fff"
        >
          Cut to {fmtH(plan.idealH)}″
        </text>
      </g>

      <g>
        <rect
          x={W - 130}
          y={tipY - 11}
          width={120}
          height={22}
          rx={11}
          fill="#fff"
          stroke={M_BRAND}
          strokeWidth="1.5"
        />
        <text
          x={W - 70}
          y={tipY + 4}
          textAnchor="middle"
          fontSize="11"
          fontWeight="600"
          fill={M_BRAND_700}
        >
          Mow when {plan.oneThirdMax.toFixed(1)}″
        </text>
      </g>

      <text x={W - 8} y={H - 6} textAnchor="end" fontSize="10" fill={M_MUTED}>
        soil
      </text>
    </svg>
  )
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: 2,
          background: swatch,
          border: `1px solid ${M_RULE}`,
        }}
      />
      {label}
    </span>
  )
}
